import React, { useState, useEffect, useMemo } from 'react';
import './style.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosInstance';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import Typography from '../common/StyledTypography';
import { Form, withFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormikPasswordInput } from '../common/FormikComponent';
import Info from 'apollo-react-icons/Info';
import Tooltip from 'apollo-react/components/Tooltip';
import { setupPasswordSchema } from '../../validation/auth';
import { toast } from 'react-toastify';
import AuthLayout from './AuthLayout';

const initialValues = {
  tempPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const onFormSubmit = async (
  values,
  { setSubmitting, setFieldError, props }
) => {
  const { t } = props;
  try {
    const response = await axios.post('/api/v1/auth/setup-password', {
      token: props.token,
      tempPassword: values.tempPassword,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    });

    if (response.data.success) {
      if (response.data.requiresOTP && response.data.verificationToken) {
        toast.success(
          t('setupPassword.otpSent', {
            defaultValue:
              'Password set successfully! Redirecting to email verification...',
          })
        );
        setTimeout(() => {
          props.navigate(
            `/verify-otp?token=${response.data.verificationToken}`
          );
        }, 1500);
      } else {
        toast.success(t('setupPassword.success'));
        setTimeout(() => {
          props.navigate('/login');
        }, 1500);
      }
    } else {
      toast.error(response.data.message || t('setupPassword.failed'));
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || t('setupPassword.failed');
    toast.error(errorMessage);
    if (errorMessage.includes('temp password')) {
      setFieldError('tempPassword', errorMessage);
    } else if (errorMessage.includes('confirm')) {
      setFieldError('confirmPassword', errorMessage);
    } else {
      setFieldError('newPassword', errorMessage);
    }
  }
  setSubmitting(false);
};

const SetupPassword = (props) => {
  const { token, navigate, isSubmitting, handleSubmit, t } = props;
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    const verifyTokenEffect = async () => {
      if (!token) {
        setTokenValid(false);
        toast.error(t('setupPassword.invalidSetupLink'));
        setTimeout(() => {
          navigate('/login');
        }, 1000);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get('/api/v1/auth/verify-setup-token', {
          params: { token },
        });

        if (response.data.success) {
          setTokenValid(true);
        } else {
          throw new Error(
            response.data.message || t('setupPassword.invalidOrExpiredLink')
          );
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setTokenValid(false);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          t('setupPassword.errorVerifyingLink');
        toast.error(errorMessage);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    verifyTokenEffect();
  }, [token, navigate, t]);

  if (loading || tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Typography variant="bodySmall" style={{ margin: '8px 0' }}>
            {t('setupPassword.verifyingLink')}
          </Typography>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return null;
  }

  return (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2 className="signInTitle">{t('setupPassword.title')}</h2>
            <p className="signInDesc">{t('setupPassword.description')}</p>
          </div>
        </Grid>

        <Grid container className="formContainer">
          <Grid container justifyContent="center">
            <Grid size={12}>
              <Form noValidate onSubmit={handleSubmit}>
                <FormikPasswordInput
                  name="tempPassword"
                  key="tempPassword"
                  label={
                    <span className="formLabelWithIcon">
                      {t('setupPassword.tempPasswordLabel')}
                      <Tooltip
                        title={t('setupPassword.tempPasswordTooltip')}
                        placement="top"
                      >
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  fullWidth
                  disabled={isSubmitting}
                  placeholder={t('setupPassword.tempPasswordPlaceholder')}
                />
                <FormikPasswordInput
                  name="newPassword"
                  key="newPassword"
                  label={
                    <span className="formLabelWithIcon">
                      {t('setupPassword.newPasswordLabel')}
                      <Tooltip
                        title={t('setupPassword.newPasswordTooltip')}
                        placement="top"
                      >
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  fullWidth
                  disabled={isSubmitting}
                />
                <FormikPasswordInput
                  name="confirmPassword"
                  key="confirmPassword"
                  label={
                    <span className="formLabelWithIcon">
                      {t('setupPassword.confirmPasswordLabel')}
                      <Tooltip
                        title={t('setupPassword.confirmPasswordTooltip')}
                        placement="top"
                      >
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  fullWidth
                  disabled={isSubmitting}
                />

                <Button
                  variant="primary"
                  className="loginButton"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('setupPassword.settingUp')
                    : t('setupPassword.submitButton')}
                </Button>
              </Form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

// Create FormikSetupPassword with translation support
const createFormikSetupPassword = (t) =>
  withFormik({
    validationSchema: toFormikValidationSchema(setupPasswordSchema(t)),
    mapPropsToValues: () => initialValues,
    handleSubmit: onFormSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  })(SetupPassword);

export default function SetupPasswordWithNavigate(props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  // Memoize FormikSetupPassword to avoid recreating HOC on every render
  const FormikSetupPassword = useMemo(() => createFormikSetupPassword(t), [t]);

  return (
    <FormikSetupPassword {...props} navigate={navigate} token={token} t={t} />
  );
}
